import { DownloadService } from "@/services/admin-services/DownloadService";
import { Download } from "@/services/admin-services/models/Download";
import { LocaleService } from "@/services/global-services";
import { Language } from "@/services/global-services/models";
import { useDataStore } from "@/stores";
import { getBytes } from "@/utils/getBytes";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    LinearProgress,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { AxiosProgressEvent, CanceledError } from "axios";
import { ChangeEvent, FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { FileDrop } from "react-file-drop";
import DownloadDisciplinesDropdown from "./components/DownloadDisciplinesDropdown";
import DownloadLanguagesDropdown from "./components/DownloadLanguagesDropdown";
import DownloadSoftwareDropdown from "./components/DownloadSoftwareDropdown";
import DownloadUseCasesDropdown from "./components/DownloadUseCasesDropdown";
import DownloadVersionsDropdown from "./components/DownloadVersionsDropdown";

interface CreateDialogProps {
    open: boolean;
    languages: Array<Language>;
    onClose: () => void;
    onSubmit: (file: Download) => void;
}

export const CreateDialog = ({ open, languages, onClose, onSubmit }: CreateDialogProps) => {
    const [versionDataLoading, setVersionDataLoading] = useState<boolean>(false);
    const versions = useDataStore((state) => state.versions);
    const disciplines = useDataStore((state) => state.disciplines);
    const software = useDataStore((state) => state.software);
    const useCases = useDataStore((state) => state.useCases);

    const setDisciplines = useDataStore((state) => state.setDisciplines);
    const setSoftware = useDataStore((state) => state.setSoftware);
    const setUseCases = useDataStore((state) => state.setUseCases);

    const [file, setFile] = useState<File | null>(null);
    const [filename, setFilename] = useState<string>("");
    const [selectedVersions, setSelectedVersions] = useState<string[]>(
        versions.filter((x) => x.isDefault).map((x) => x.name)
    );
    const [language, setLanguage] = useState<string>("");
    const [selectedDisciplines, setSelectedDisciplines] = useState<Array<string>>([]);
    const [selectedSoftware, setSelectedSoftware] = useState<Array<string>>([]);
    const [selectedUseCases, setSelectedUseCases] = useState<Array<string>>([]);

    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const abortRef = useRef<AbortController | null>(null);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

    const downloadService = new DownloadService("int");
    const localeService = useMemo(() => new LocaleService("int", "en"), []);

    useEffect(() => {
        if (!file) return;
        setFilename(file.name);
    }, [file]);

    useEffect(() => {
        if (!selectedVersions) return;

        setVersionDataLoading(true);

        const promises: Promise<void>[] = [];

        selectedVersions.forEach((selectedVersion) => {
            if (disciplines[selectedVersion] && software[selectedVersion] && useCases[selectedVersion]) return;

            promises.push(
                Promise.all([
                    localeService.getDisciplines(selectedVersion),
                    localeService.getSoftware(selectedVersion),
                    localeService.getUseCases(selectedVersion),
                ]).then(([disciplines, software, useCases]) => {
                    setSelectedDisciplines([]);
                    setDisciplines(selectedVersion, disciplines);
                    setSelectedSoftware([]);
                    setSoftware(selectedVersion, software);
                    setUseCases(selectedVersion, useCases);
                })
            );
        });

        Promise.all(promises).then(() => setVersionDataLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVersions]);

    const onFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { files } = event.target;
        if (!files) return;
        setFile(files[0]);
    };

    const onFileSelect = () => {
        if (!inputRef.current) return;
        inputRef.current.click();
    };

    const onFileDrop = (files: FileList | null) => {
        if (!files) return;
        setFile(files[0]);
    };

    const onFormSubmit = (event: FormEvent) => {
        event.preventDefault();

        if (!filename || !language || !file) return;

        setIsSaving(true);

        const form = new FormData();
        form.append("Name", filename);
        form.append("File", file);
        selectedVersions.forEach((x) => form.append("Versions", x));
        selectedDisciplines.forEach((x) => form.append("Disciplines", x));
        selectedSoftware.forEach((x) => form.append("Software", x));
        selectedUseCases.forEach((x) => form.append("UseCases", x));

        const abort = new AbortController();
        abortRef.current = abort;

        downloadService
            .create(language, form, abort, onUploadProgress)
            .then((file) => {
                setIsSaving(false);
                abortRef.current = null;
                onSubmit(file);
            })
            .catch((err) => {
                if (err instanceof CanceledError) return;
                throw err;
            });
    };

    const onUploadProgress = (event: AxiosProgressEvent) => {
        setUploadProgress((event.progress || 0) * 100);
    };

    const onCancel = () => {
        abortRef.current?.abort();
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth={"sm"} fullScreen={fullScreen}>
            <form onSubmit={(event) => onFormSubmit(event)}>
                <DialogTitle>Add new download</DialogTitle>
                <DialogContent
                    dividers
                    sx={{
                        pointerEvents: isSaving ? "none" : "auto",
                    }}
                >
                    <FileDrop onTargetClick={onFileSelect} onDrop={onFileDrop}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                borderColor: "text.secondary",
                                borderWidth: 2,
                                borderStyle: "dashed",
                                borderRadius: 5,
                                minHeight: 150,
                                placeContent: "center",
                                alignItems: "center",
                                opacity: isSaving ? 0.5 : 1,
                                padding: 3,
                            }}
                        >
                            {file ? (
                                <>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ userSelect: "none" }}>
                                        {file.name}
                                    </Typography>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ userSelect: "none" }}>
                                        ({getBytes(file.size)})
                                    </Typography>
                                </>
                            ) : (
                                <Typography variant="subtitle2" color="text.secondary" sx={{ userSelect: "none" }}>
                                    DROP FILE HERE OR BROWSE
                                </Typography>
                            )}
                        </Box>
                    </FileDrop>
                    <input
                        value=""
                        style={{ visibility: "hidden", opacity: 0 }}
                        ref={inputRef}
                        type="file"
                        onChange={onFileInputChange}
                    />

                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                autoFocus
                                fullWidth
                                id={`download-filename`}
                                value={filename}
                                onChange={(event) => setFilename(event.target.value)}
                                label={`Filename`}
                                type="text"
                                variant="outlined"
                                disabled={isSaving}
                            />
                        </Grid>
                    </Grid>
                    <Divider sx={{ marginY: 3 }} />
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                            <DownloadVersionsDropdown
                                value={selectedVersions}
                                onChange={(e) => setSelectedVersions(e.target.value as string[])}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <DownloadLanguagesDropdown
                                items={languages}
                                value={language}
                                onChange={(e) => setLanguage(e.target.value as string)}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <DownloadDisciplinesDropdown
                                selectedVersions={selectedVersions}
                                value={selectedDisciplines}
                                onChange={(e) => setSelectedDisciplines(e.target.value as string[])}
                                loading={versionDataLoading}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <DownloadSoftwareDropdown
                                selectedVersions={selectedVersions}
                                value={selectedSoftware}
                                onChange={(e) => setSelectedSoftware(e.target.value as string[])}
                                loading={versionDataLoading}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <DownloadUseCasesDropdown
                                selectedVersions={selectedVersions}
                                value={selectedUseCases}
                                onChange={(e) => setSelectedUseCases(e.target.value as string[])}
                                loading={versionDataLoading}
                            />
                        </Grid>
                    </Grid>

                    {isSaving && (
                        <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 15, mt: 2 }} />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCancel}>Cancel</Button>
                    <Button variant="contained" type="submit" disabled={isSaving}>
                        Add
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};
